import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./services/firebase";
import { useAppState } from "./context/AppContext";

/**
 * Determines whether the signed-in user is an admin of the practice in the
 * current route (`/:slug`).
 *
 * The user's role lives at users/{uid}/practices/{slug}.role. We read it
 * directly (via react-query) rather than from state.user.practices, because
 * PrivateRoute can transiently replace state.user with a bare auth user that
 * has no practices attached when a protected route mounts.
 *
 * @returns {{ isAdmin: boolean, isReady: boolean }}
 *   isReady is false while the role is still being fetched, so callers can show
 *   a loading state instead of prematurely treating the user as a non-admin.
 */
export function useIsPracticeAdmin() {
  const { slug } = useParams();
  const { state } = useAppState();
  const uid = state.user?.uid;
  const enabled = !!uid && !!slug;

  const { data: role, isLoading } = useQuery({
    queryKey: ["practice-role", uid, slug],
    queryFn: async () => {
      const snap = await getDoc(doc(db, "users", uid, "practices", slug));
      return snap.exists() ? snap.data().role || null : null;
    },
    enabled,
    staleTime: 1000 * 60 * 5,
  });

  return {
    isAdmin: role === "admin",
    isReady: enabled ? !isLoading : false,
  };
}
